import { takeEvery, call, put } from "redux-saga/effects";
import {
  START_VIDEO_RENDER_REQUEST,
  GET_RENDER_PROGRESS_REQUEST,
  GET_RENDER_DOWNLOAD_URL_REQUEST,
  startVideoRenderSuccess,
  startVideoRenderFailure,
  getRenderProgressSuccess,
  getRenderProgressFailure,
  getRenderDownloadUrlSuccess,
  getRenderDownloadUrlFailure,
} from "../actions/videoRenderActions";
import {
  startVideoRender as startVideoRenderApi,
  getRenderProgress as getRenderProgressApi,
  getRenderDownloadUrl as getRenderDownloadUrlApi,
} from "../apis/renderApi";

/**
 * Read brand typography from localStorage THEME and inject into inputProps
 * so the Remotion Lambda composition can apply the creator's brand fonts.
 */
function getBrandTypographyForRender() {
  try {
    const stored = localStorage.getItem("THEME");
    if (!stored) return null;
    const theme = JSON.parse(stored);
    const t = theme?.typography;
    if (!t) return null;
    return {
      fontHeading:   t.fontHeading   || t.fontPrimary || "Inter",
      fontBody:      t.fontBody      || "Open Sans",
      fontPrimary:   t.fontPrimary   || "Inter",
      fontSecondary: t.fontSecondary || "Roboto",
      fontMonospace: t.fontMonospace || "JetBrains Mono",
    };
  } catch (e) {
    console.warn("videoRenderSaga - Could not read brand typography:", e);
    return null;
  }
}

// Saga to handle starting a video render
function* startVideoRenderSaga(action) {
  try {
    // Inject brand typography into inputProps before sending to Lambda
    let payload = action.payload;
    const brandTypography = getBrandTypographyForRender();
    if (brandTypography) {
      const existingProps =
        typeof payload.inputProps === "string"
          ? JSON.parse(payload.inputProps || "{}")
          : payload.inputProps || {};
      payload = {
        ...payload,
        inputProps: { ...existingProps, brandTypography },
      };
      console.log("🎬 SAGA: Brand typography injected into inputProps:", brandTypography);
    }

    console.log("🎬 SAGA: startVideoRenderSaga - ACTION RECEIVED!", {
      actionType: action.type,
      timestamp: new Date().toISOString(),
      payload: {
        ...payload,
        inputPropsKeys: payload.inputProps
          ? Object.keys(payload.inputProps)
          : null,
      },
    });
    console.log(
      "🎬 SAGA: Calling startVideoRenderApi with payload:",
      payload
    );

    const response = yield call(startVideoRenderApi, payload);

    console.log("✅ SAGA: startVideoRenderApi returned:", response);
    console.log("🎬 SAGA: Dispatching startVideoRenderSuccess action...");

    yield put(startVideoRenderSuccess(response));

    console.log("✅ SAGA: startVideoRenderSuccess action dispatched");
  } catch (error) {
    console.error("❌ SAGA: Error in startVideoRenderSaga:", error);
    console.error("❌ SAGA: Error details:", {
      message: error?.message,
      stack: error?.stack,
      name: error?.name,
      error: error,
    });
    const errorMessage = error?.message || error?.toString() || "Unknown error";
    console.log("🎬 SAGA: Dispatching startVideoRenderFailure action...");
    yield put(startVideoRenderFailure(errorMessage));
    console.log("✅ SAGA: startVideoRenderFailure action dispatched");
  }
}

// Saga to handle getting render progress
function* getRenderProgressSaga(action) {
  try {
    console.log("📊 SAGA: getRenderProgressSaga - ACTION RECEIVED!", {
      actionType: action.type,
      payload: action.payload,
      timestamp: new Date().toISOString(),
    });
    console.log("📊 SAGA: Calling getRenderProgressApi...");

    const response = yield call(getRenderProgressApi, action.payload);

    console.log("✅ SAGA: getRenderProgressApi returned:", response);
    console.log("📊 SAGA: Dispatching getRenderProgressSuccess action...");

    yield put(getRenderProgressSuccess(response));

    console.log("✅ SAGA: getRenderProgressSuccess action dispatched");
  } catch (error) {
    console.error("❌ SAGA: Error in getRenderProgressSaga:", error);
    const errorMessage = error?.message || error?.toString() || "Unknown error";
    console.log("📊 SAGA: Dispatching getRenderProgressFailure action...");
    yield put(getRenderProgressFailure(errorMessage));
    console.log("✅ SAGA: getRenderProgressFailure action dispatched");
  }
}

// Saga to handle getting render download URL
function* getRenderDownloadUrlSaga(action) {
  try {
    console.log("🔗 videoRenderSaga - Getting download URL:", action.payload);
    const response = yield call(getRenderDownloadUrlApi, action.payload);
    console.log("✅ videoRenderSaga - Download URL retrieved:", response);
    yield put(getRenderDownloadUrlSuccess(response));
  } catch (error) {
    console.error("❌ videoRenderSaga - Error getting download URL:", error);
    const errorMessage = error?.message || error?.toString() || "Unknown error";
    yield put(getRenderDownloadUrlFailure(errorMessage));
  }
}

// Watcher Saga for video render actions
function* watchVideoRenderActions() {
  console.log("🎬 SAGA: watchVideoRenderActions - Watcher saga REGISTERED!", {
    timestamp: new Date().toISOString(),
    listeningForActions: {
      START_VIDEO_RENDER_REQUEST,
      GET_RENDER_PROGRESS_REQUEST,
      GET_RENDER_DOWNLOAD_URL_REQUEST,
    },
  });
  yield takeEvery(START_VIDEO_RENDER_REQUEST, startVideoRenderSaga);
  yield takeEvery(GET_RENDER_PROGRESS_REQUEST, getRenderProgressSaga);
  yield takeEvery(GET_RENDER_DOWNLOAD_URL_REQUEST, getRenderDownloadUrlSaga);
  console.log("✅ SAGA: All watchers registered and ready");
}

export default watchVideoRenderActions;
